import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import fm from 'format-message';
import PropTypes from 'prop-types';
import CmsModules from 'libs/storyblok/content/Modules';
import headerHeights from 'config/branding/header-heights';
import textStyles from 'config/branding/textStyles';
import useAppQuery from 'queries/useAppQuery';
import useSortOptionOrderQuery from 'queries/useSortOptionOrderQuery';
import SubCategoryButtons from '@activebrands/core-web/components/buttons/SubCategoryButtons';
import AnimatedOverlay from '@activebrands/core-web/components/overlays/AnimatedOverlay';
import { facetMap } from '@activebrands/core-web/config/filters';
import media from '@activebrands/core-web/config/media';
import { ProductsProvider, useProducts } from '@activebrands/core-web/contexts/ProductsContext';
import usePagination from '@activebrands/core-web/hooks/usePagination';
import Events from '@activebrands/core-web/libs/Events';
import { PageEvents } from '@activebrands/core-web/libs/Events/types';
import RichText from '@activebrands/core-web/libs/storyblok/RichText';
import { styled, useStyletron } from '@activebrands/core-web/libs/styletron';
import { sortFacetOptions } from '@activebrands/core-web/utils/faceting';
import responsiveTextStyles from '@activebrands/core-web/utils/responsive-text-styles';
import scrollToTopOfRef from '@activebrands/core-web/utils/scroll-to-top-of-ref';
import isObjectEmpty from '@grebban/utils/object/isEmpty';
import Breadcrumbs from 'components/Breadcrumbs';
import StickyFilterBar from 'components/product-listing/StickyFilterBar';
import Heading from 'components/text/Heading';
import CategoryFacetGroups from 'templates/CategoryPage/CategoryFacetGroups';
import CategoryFilterBar from 'templates/CategoryPage/CategoryFilterBar';
import CategoryProductsListing from 'templates/CategoryPage/CategoryProductsListing';
import CategorySortOptions from 'templates/CategoryPage/CategorySortOptions';
import transformParagraph from '@activebrands/core-web/libs/storyblok/utils/transform-paragraph';

const Wrapper = styled('div', {
    position: 'relative',
});

const HeroSection = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '0px',
    marginBottom: '24px',
    padding: '12px var(--margin-content) 0px',

    [media.min['desktop.sm']]: {
        marginBottom: '56px',
        marginTop: '40px',
        padding: '0px var(--margin-content)',
    },
});

const Description = styled('div', {
    width: '100%',
    ...textStyles['Secondary/14_130_-0175'],

    [media.min['desktop.sm']]: {
        width: '25%',
    },
});

const HeadingLabel = styled('div', {
    ...responsiveTextStyles(['Primary/40_-05', null, null, null, 'Primary/48_-09']),
    marginBottom: '32px',

    [media.min['desktop.sm']]: {
        marginBottom: '0',
    },
});

const CategoryPage = ({ pageContext, previewDataOverride }) => {
    const [css] = useStyletron();
    const filterBarRef = useRef();
    const { breadcrumbs, content, relationships, id, name, slug, path, url } =
        previewDataOverride || pageContext.page.data;
    const contactId = useSelector(state => state?.auth?.user?.contactId);
    const gridWrapperRef = useRef();
    const { headerHeight } = headerHeights;

    const {
        description,
        faceting,
        gridContent,
        headingLabel,
        manuallyAddedCategoryButtons,
        noProductsButtons,
        noProductsTitle,
        overrideCategoryButtons,
        pageContentAboveProducts,
        pageContentBelowProducts,
    } = content;

    const descriptionParagraph = transformParagraph({ paragraph: description })

    const priceFacetRange = faceting?.products?.reduce(
        ({ max }, { price }, i) => ({
            min: 0,
            max: !i ? price?.salePrice : max < price?.salePrice ? price?.salePrice : max,
        }),
        { min: 0, max: 0 }
    );
    const sortOptionOrderMap = useSortOptionOrderQuery();
    const sortedFacetOptions = useMemo(
        () => sortFacetOptions(faceting?.facets, sortOptionOrderMap),
        [faceting?.facets]
    );

    const application = useAppQuery();

    const ssrPagination = !!pageContext?.page?.ssrPagination;

    const currentCategory = {
        name,
        path,
        slug,
        id,
    };

    const noChildCategories = isObjectEmpty(relationships.children);
    const parentButton = noChildCategories ? relationships.parent : currentCategory;
    const childButtons = noChildCategories ? relationships.siblings : relationships.children;
    let relatedButtons;

    if (overrideCategoryButtons) {
        relatedButtons = manuallyAddedCategoryButtons?.map(item => ({
            id: item._uid,
            isActive: item._uid === id,
            label: item.label,
            to: `${item.link.url}`,
        }));
    } else {
        relatedButtons = childButtons?.map(item => ({
            id: item.id,
            isActive: item.id === id,
            label: item.name,
            to: `${item.path}`,
        }));
    }

    if (parentButton?.name) {
        relatedButtons.unshift({
            id: parentButton.id,
            isActive: parentButton.id === id,
            label: fm(`View all`, parentButton),
            to: parentButton.path !== null ? parentButton.path : url,
        });
    }

    useEffect(() => {
        if (pageContext) {
            Events.trigger(PageEvents.VIEW, {
                currencyCode: application.pricelist?.currency,
                locale: application.application.locale,
                pageTitle: pageContext.page.data.name,
                userId: contactId,
            });
        }
    }, [pageContext?.page]);

    const { products, cacheKey } = useProducts();
    const { page } = usePagination(products, { cacheKey });
    const showPageContent = page === 1;

    return (
        <Wrapper>
            <HeroSection>
                {!!breadcrumbs && <Breadcrumbs $style={{ marginBottom: '24px' }} items={breadcrumbs} />}
                <Heading
                    $style={{
                        display: ['block', null, null, null, null, 'flex'],
                        justifyContent: 'space-between',
                        width: '100%',
                        marginBottom: '24px',
                        color: 'var(--color-text-hero)',
                    }}
                    as="h1"
                >
                    <HeadingLabel>{headingLabel}</HeadingLabel>
                    {descriptionParagraph.text && (
                        <Description {...descriptionParagraph.sbEditable}   >
                            <RichText data={descriptionParagraph.text} />
                        </Description>
                    )}
                </Heading>
                {!!relatedButtons.length && (
                    <SubCategoryButtons
                        $style={{
                            height: '44px', // Accounts for the 1px border on the buttons
                            padding: ['0 var(--margin-content)', null, null, null, '0'],
                            margin: ['0 -12px', null, null, null, '0'],
                            gap: 0,
                            '::after': {
                                display: ['block', null, null, null, 'none'],
                                content: '""',
                                position: 'absolute',
                                right: 0,
                                width: '12px',
                                height: '42px',
                                background: [
                                    'linear-gradient(270deg, var(--color-bg) 0%, var(--color-bg-gradient) 100%)',
                                    null,
                                    null,
                                    null,
                                    null,
                                    'none',
                                ],
                            },
                            '::before': {
                                display: ['block', null, null, null, 'none'],
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                width: '12px',
                                height: '42px',
                                background: [
                                    'linear-gradient(90deg, var(--color-bg) 0%, var(--color-bg-gradient) 100%)',
                                    null,
                                    null,
                                    null,
                                    null,
                                    'none',
                                ],
                            },
                        }}
                        buttonSize="md"
                        buttonStyle={{
                            height: '42px',
                        }}
                        fontStyling={textStyles['Misc/16_100_-02']}
                        id="pagination-top"
                        items={relatedButtons}
                        theme="default"
                    />
                )}
            </HeroSection>
            {showPageContent && pageContentAboveProducts ? (
                <div className={css({ marginBottom: ['24px', null, null, null, null, '32px'] })}>
                    <CmsModules modules={pageContentAboveProducts} />
                </div>
            ) : null}
            {pageContext ? (
                <div
                    ref={gridWrapperRef}
                    className={css({ position: 'relative' })}
                    id={relatedButtons?.length > 1 ? undefined : 'pagination-top'}
                >
                    <ProductsProvider
                        allProducts={faceting.products}
                        facets={sortedFacetOptions.filter(f => !!f.data.length)}
                        facetMap={facetMap}
                    >
                        <CategoryFilterBar
                            $style={{
                                position: 'sticky',
                                padding: '0 var(--margin-content)',
                                top: 'var(--height-header)',
                                backgroundColor: 'var(--color-bg-filterbar)',
                            }}
                            ref={filterBarRef}
                            filterOverlayId="category-facet-groups"
                            sortOverlayId="category-sort-options"
                        />
                        <CategoryProductsListing
                            gridContent={gridContent}
                            noProductsButtons={noProductsButtons}
                            noProductsTitle={noProductsTitle}
                            ssrPagination={ssrPagination}
                            showPageContent={showPageContent}
                        />
                        <StickyFilterBar
                            filterOverlayId="category-facet-groups"
                            sortOverlayId="category-sort-options"
                            staticFilterBarRef={filterBarRef}
                        />
                        <AnimatedOverlay
                            $style={{ zIndex: 'var(--zindex-filter)' }}
                            animation="slideLTR"
                            component={CategoryFacetGroups}
                            id="category-facet-groups"
                            priceFacetRange={priceFacetRange}
                            onHandleFilter={() => scrollToTopOfRef(gridWrapperRef, headerHeight)}
                        />
                        <AnimatedOverlay
                            lockScroll
                            $style={{ zIndex: 'var(--zindex-filter)' }}
                            animation="slideRTL"
                            component={CategorySortOptions}
                            id="category-sort-options"
                            priceFacetRange={priceFacetRange}
                        />
                    </ProductsProvider>
                </div>
            ) : (
                <>
                    {/* Will be visible when in preview mode */}
                    <div className={css({ padding: [' 64px 8px', null, null, null, null, '64px 24px'] })}>
                        <p className={css({ margin: '0px auto 64px', textAlign: 'center' })}>
                            Products are unavailable in preview
                        </p>
                        <CmsModules isGrid modules={gridContent} />
                    </div>
                </>
            )}
            {showPageContent && pageContentBelowProducts ? <CmsModules modules={pageContentBelowProducts} /> : null}
        </Wrapper>
    );
};

CategoryPage.propTypes = {
    pageContext: PropTypes.object,
    previewDataOverride: PropTypes.object,
};

export default CategoryPage;
